import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import useGlobalStyles from '../useGlobalStyles';
import ProfilePictureSelector from './profilePictureSelector';
import useCreateAccountStyles from './useCreateAccountStyles';
import useLoginStyles from '../login/useLoginStyles';
import useCreateAccount from '../../hooks/useCreateAccount';

/**
 * Create Account Component contains a form that allows the user to create an account with a username,
 * password, and profile picture.
 * @returns A form to create an account with a username, password, and profile picture.
 */
const CreateAccount = () => {
  const { classes } = useCreateAccountStyles();
  const { classes: loginClasses } = useLoginStyles();
  const { classes: globalClasses } = useGlobalStyles();

  const {
    username,
    password,
    profile,
    handleInputChange,
    handleSubmit,
    handleLoginNav,
    handleProfileSelect,
    status,
    error,
    success,
  } = useCreateAccount();

  return (
    <div className={loginClasses.container}>
      <div className={`${globalClasses.h2} ${loginClasses.welcome}`}>
        Welcome to{' '}
        <img
          src={`${process.env.PUBLIC_URL}/assets/branding/login-logo.png`}
          alt='Logo'
          className={loginClasses.logo}
        />
        !
      </div>
      <div className={classes.sectionContainer}>
        <div className={`${globalClasses.h3} ${loginClasses.text}`}>Create an Account</div>
        <form onSubmit={handleSubmit} className={loginClasses.form}>
          <p className={`${globalClasses.h5} ${loginClasses.labelText}`}>
            Choose your Profile Picture
          </p>
          <ProfilePictureSelector selectedPicture={profile} onSelectPicture={handleProfileSelect} />
          <hr className={classes.dividerLine} />
          <label
            htmlFor='usernameInput'
            className={`${globalClasses.h5} ${loginClasses.labelText}`}>
            Username
          </label>
          <input
            type='text'
            value={username}
            onChange={e => handleInputChange(e, 'username')}
            placeholder='Enter your chosen username'
            required
            className={`${globalClasses.input} ${loginClasses.formInput}`}
            id='usernameInput'
          />
          <label
            htmlFor='passwordInput'
            className={`${globalClasses.h5} ${loginClasses.labelText}`}>
            Password
          </label>
          <input
            type='password'
            value={password}
            onChange={e => handleInputChange(e, 'password')}
            placeholder='Enter a password'
            required
            className={`${globalClasses.input} ${loginClasses.formInput}`}
            id='passwordInput'
          />
          {error && (
            <p className={loginClasses.errorMessage}>
              {error.includes('409')
                ? 'Username already exists. Please try again.'
                : 'Unable to create account. Please try again later.'}
            </p>
          )}
          {success && (
            <p className={`${globalClasses.error} ${classes.successMessage}`}>Account Created!</p>
          )}
          <Button
            type='submit'
            className={`${globalClasses.button} ${loginClasses.submitButton}`}
            disabled={status === 'loading'}
            aria-busy={status === 'loading'}
            aria-label={status === 'loading' ? 'Submitting...' : 'Submit'}
            style={{ cursor: status === 'loading' ? 'none' : 'pointer' }}>
            {status === 'loading' ? (
              <CircularProgress size={'1.5rem'} style={{ color: 'white' }} />
            ) : (
              'Submit'
            )}
          </Button>
        </form>
        <div className={loginClasses.dividerOr}>
          <hr className={loginClasses.dividerLine} />
          <span className={loginClasses.dividerText}>OR</span>
          <hr className={loginClasses.dividerLine} />
        </div>
        <p className={globalClasses.h6}>Already have an account?</p>
        <Button
          onClick={handleLoginNav}
          className={`${globalClasses.button} ${loginClasses.switchButton}`}>
          Log In
        </Button>
      </div>
    </div>
  );
};

export default CreateAccount;
